'use client';

import { Download, ArrowRight, Shield, BarChart3 } from 'lucide-react';

export default function HowItWorksSection() {
  const steps = [
    {
      number: '01',
      icon: Download,
      color: 'rose',
      title: 'Install in minutes',
      description: 'One line in a Next.js app, a Cloudflare Worker, or a Docker container in front of any backend. No re-architecture.',
      detail: 'npm i @silker-ai/agent',
    },
    {
      number: '02',
      icon: Shield,
      color: 'blue',
      title: 'Detect locally, block instantly',
      description: 'Every request is inspected where your app runs. Threats - including prompt injection and data leaks - are blocked in real-time. Detection runs on your infra, not ours.',
      detail: 'request → inspect → allow / 403',
    },
    {
      number: '03',
      icon: BarChart3,
      color: 'emerald',
      title: 'Monitor & fix',
      description: 'See blocked threats, data-leak alerts, and traffic on the dashboard. AI Copilot explains each issue and suggests the fix.',
      detail: 'platform.silkerai.com',
    },
  ];

  const colorMap: Record<string, { bg: string; border: string; text: string; line: string }> = {
    rose: { bg: 'bg-rose-500/10', border: 'border-rose-500/20', text: 'text-rose-400', line: 'from-rose-500/50' },
    blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-400', line: 'from-blue-500/50' },
    emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-400', line: 'from-emerald-500/50' },
  };

  return (
    <section className="sm:py-24 pt-16 pb-16 relative">
      <div className="sm:px-6 lg:px-8 max-w-5xl mx-auto px-4">
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-rose-400 px-3 py-1 rounded-none bg-rose-500/10 border border-rose-500/20">
            How It Works
          </span>
          <h2 className="sm:text-4xl md:text-5xl text-3xl font-semibold tracking-tight text-center mt-4 text-white">
            Three steps to runtime security
          </h2>
          <p className="sm:text-lg text-base text-neutral-400 text-center mt-4 max-w-2xl mx-auto leading-relaxed">
            Install, detect, monitor. Your traffic stays on your infrastructure - we only receive security events.
          </p>
        </div>

        <div className="space-y-6">
          {steps.map((step, index) => {
            const colors = colorMap[step.color];
            const IconComponent = step.icon;
            return (
              <div key={index} className="relative">
                <div className="flex items-start gap-6 sm:gap-8 p-6 sm:p-8 rounded-none border border-white/5 bg-neutral-900/20 hover:bg-neutral-900/40 transition-colors duration-300 group">
                  <div className="shrink-0 flex flex-col items-center gap-3">
                    <div className={`w-14 h-14 rounded-none ${colors.bg} border ${colors.border} flex items-center justify-center`}>
                      <IconComponent className={`w-7 h-7 ${colors.text}`} />
                    </div>
                    <span className={`text-xs font-mono font-bold ${colors.text}`}>{step.number}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                    <p className="text-sm text-neutral-400 leading-relaxed mb-3">{step.description}</p>
                    <code className="text-xs font-mono text-neutral-500 bg-black/30 px-3 py-1.5 rounded-none border border-white/5">
                      {step.detail}
                    </code>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden sm:flex items-center shrink-0 self-center">
                      <ArrowRight className="w-5 h-5 text-neutral-700 rotate-90" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
